import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getSignedUrlForFile } from '@/services/uploadService';
import { PriceRibbon } from '@/components/ui/PriceRibbon';

export interface ItemSelectionOption {
  readonly key: string;
  readonly label: string;
  readonly uploadedFileId?: string | null;
  readonly priceLabel?: string | null;
  readonly priceLoading?: boolean;
  readonly warningLabel?: string | null;
}

interface ItemSelectionHeaderProps {
  readonly title: string;
  readonly items: readonly ItemSelectionOption[];
  readonly activeKey: string | null | undefined;
  readonly onSelect: (key: string) => void;
  readonly className?: string;
}

export const ItemSelectionHeader = ({
  title,
  items,
  activeKey,
  onSelect,
  className,
}: ItemSelectionHeaderProps) => {
  const [previews, setPreviews] = useState<Record<string, string | null>>({});
  const requestedRef = useRef<Set<string>>(new Set());
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const uniqueIds = new Set(
      items
        .map(option => option.uploadedFileId)
        .filter((value): value is string => typeof value === 'string' && value.length > 0),
    );

    uniqueIds.forEach(async fileId => {
      if (requestedRef.current.has(fileId)) {
        return;
      }
      requestedRef.current.add(fileId);

      try {
        const { thumbnailSignedUrl, signedUrl } = await getSignedUrlForFile(fileId);
        if (!isMountedRef.current) {
          return;
        }
        setPreviews(prev => ({
          ...prev,
          [fileId]: thumbnailSignedUrl ?? signedUrl ?? null,
        }));
      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }
        setPreviews(prev => ({
          ...prev,
          [fileId]: null,
        }));
      }
    });
  }, [items]);

  const renderedItems = useMemo(() => {
    return items.map(option => {
      const previewUrl = option.uploadedFileId ? previews[option.uploadedFileId] : undefined;
      return {
        option,
        previewUrl,
      };
    });
  }, [items, previews]);

  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-3 border-b border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground', className)}>
      <span className="font-medium uppercase tracking-wide">{title}</span>
      <div className="flex flex-wrap gap-3">
        {renderedItems.map(({ option, previewUrl }) => {
          const isActive = option.key === activeKey;
          return (
            <Button
              key={option.key}
              type="button"
              variant="ghost"
              className={cn(
                'group relative flex h-16 min-w-[7rem] flex-col items-stretch gap-1 rounded-xl border bg-card px-2 py-2 text-left shadow-sm transition-all',
                isActive
                  ? 'border-primary/60 bg-primary/10 ring-2 ring-primary/20'
                  : 'border-border/70 hover:border-primary/40 hover:bg-primary/5 hover:text-foreground',
              )}
              onClick={() => onSelect(option.key)}
            >
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/30 transition-colors',
                    isActive ? 'border-primary/60 bg-primary/10' : 'border-border/60',
                  )}
                  aria-hidden
                >
                  {previewUrl ? (
                    <img src={previewUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <ImageIcon className={cn('h-5 w-5', isActive ? 'text-primary-600' : 'text-muted-foreground')} />
                  )}
                </span>
                <span className="flex flex-1 flex-col items-end gap-1 text-right">
                  <span className={cn('inline-flex items-center justify-end gap-1 text-xs font-semibold tracking-wide', isActive ? 'text-foreground' : 'text-muted-foreground/80')}>
                    {option.warningLabel ? (
                      <AlertTriangle
                        className="h-3.5 w-3.5 shrink-0 text-amber-600"
                        aria-label={option.warningLabel}
                      />
                    ) : null}
                    {option.label}
                  </span>
                  {option.priceLabel ? (
                    <PriceRibbon
                      label={option.priceLabel}
                      loading={option.priceLoading}
                      className="!static !translate-x-0 !translate-y-0 !text-xs !font-semibold"
                    />
                  ) : null}
                </span>
              </div>
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default ItemSelectionHeader;
